import React, {useEffect, useState} from 'react';
import apiService from '../../utils/apiService';
import { isSubActive } from '../../utils/user';
import { useSelector, useDispatch } from 'react-redux';
import { setSubscription } from '../../redux/userSlice';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchUserSubscription } from '../../utils/apiRoutes';
import LoaderBtn from '../Assets/LoaderBtn';
import { PrimaryBtn } from '../Assets/Buttons';

const Subscriptions = () => {
    const subscription = useSelector((state) => state.user.subscription);
    const userId = useSelector((state) => state.user.id);
    const [searchParams] = useSearchParams();
    const portalSession = searchParams.get('portal_session');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    // Run when user completes a portal session
    useEffect(() => {
        const updateSubscription = async () => {
            const subPayload = await fetchUserSubscription(userId);
            // Update to use new object payload format with isActive check
            const { isActive } = isSubActive(subPayload);
            dispatch(setSubscription({ subscription: subPayload, isActive }));
        };

        // Fetch subscription data and update redux store
        if (userId && portalSession && portalSession === "completed") {
            updateSubscription();
        };
    }, [portalSession, userId]);

    // Converts to Stripe timestamp to a readable date
    const convertTimestampToDate = (timestamp) => {
        const date = new Date(timestamp * 1000); // Convert from seconds to milliseconds
        return date.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric'
        });
    };

    // Function to create a customer portal session and redirect them to it
    const manageSubscription = async () => {
        setIsLoading(true);

        const body = { 
            return_url: `${window.location.origin}/profile?portal_session=completed`,
            user_id: userId
        };
        try{
            const payload = await apiService('payments/customer-portal', "POST", body, {});
            const portalUrl = payload.url;
            window.location.href = portalUrl; // Redirect to Stripe's customer portal
        }catch(error){
            // Handle all other errors
            if(error.status === 401 || error.message === 'Session Expired') {
                clearSession();
                navigate('/signin');
            } else if(error.status === 0) {
                console.error('Network error while creating customer portal:', error.message);
            } else {
                console.error(`Error creating customer portal (${error.status}):`, error.message);
            }

            setIsLoading(false);
        };
    };

    return (
        <div className="rounded-lg overflow-hidden">
            <div className="bg-gray-50 dark:bg-brand-dark-700 px-4 py-3 border-b border-gray-200 dark:border-brand-dark-600">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    My Subscriptions
                </h3>
            </div>
            <div className="bg-white dark:bg-brand-dark-800 px-4 py-5 space-y-4">
                {subscription != null? 
                    (
                        <div className="space-y-4">
                            <p className="text-gray-700 dark:text-gray-300">
                                <span className="font-medium">${subscription.amount / 100}</span> USD / {subscription.interval}
                            </p>
                            <p className="text-gray-700 dark:text-gray-300">
                                Status: <span className="font-medium">{subscription.subscription_status}</span>
                            </p>
                            <p className="text-gray-700 dark:text-gray-300">
                                {subscription.subscription_status === 'canceled' ? 'Subscription End Date: ' : 'Next Bill Date: '}
                                <span className="font-medium">{convertTimestampToDate(subscription.current_period_end)}</span>
                            </p>
                            <LoaderBtn
                                btnClasses="inline-flex items-center justify-center rounded-md border border-transparent bg-brand-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-red-600 focus:outline-none focus:ring-2 focus:ring-brand-red-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                isLoading={isLoading}
                                handler={manageSubscription}
                                defaultText="Manage Subscription"
                                loadingText="Loading"
                            />
                        </div>
                    )
                    : 
                    (
                        <div className="space-y-4">
                            <p className="text-gray-700 dark:text-gray-300">No active subscriptions</p>
                            <PrimaryBtn
                                text="View Pricing"
                                onClick={() => navigate('/pricing')}
                            />
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default Subscriptions;